import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import {
  catalogService,
  type CatalogGroups,
  type CatalogKind,
  type CatalogResource,
  type CatalogShare,
  type CatalogUse,
  type CatalogVisibility,
} from '../services/api'

const KINDS: Array<{ id: CatalogKind; label: string }> = [
  { id: 'document', label: 'Document' },
  { id: 'repository', label: 'Dépôt' },
  { id: 'cloud', label: 'Cloud' },
  { id: 'other', label: 'Autre' },
]

const VISIBILITIES: Array<{ id: CatalogVisibility; label: string }> = [
  { id: 'private', label: 'Privée' },
  { id: 'shared', label: 'Partagée' },
  { id: 'public', label: 'Publique' },
]

function kindLabel(kind: CatalogKind): string {
  return KINDS.find((item) => item.id === kind)?.label ?? kind
}

function visibilityLabel(visibility: CatalogVisibility): string {
  return VISIBILITIES.find((item) => item.id === visibility)?.label ?? visibility
}

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string } } } }).response?.data
    if (data?.error?.message) {
      return data.error.message
    }
  }
  return error instanceof Error ? error.message : 'La requête a échoué'
}

function usageOf(error: unknown): CatalogUse[] | null {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { error?: { usage?: CatalogUse[] } } } }).response
    if (response?.status === 409 && response.data?.error?.usage) {
      return response.data.error.usage
    }
  }
  return null
}

function usageText(usage: CatalogUse[]): string {
  return usage.map((item) => `${item.roleName} (${item.circleName}) — instance ${item.instanceName}, agent ${item.agentName}`).join('\n')
}

function confirmTwice(usage: CatalogUse[]): boolean {
  const lines = usageText(usage)
  return window.confirm(`Cette ressource est utilisée :\n${lines}\n\nPremière confirmation : continuer la modification ?`)
    && window.confirm(`Deuxième confirmation. Rôles et instances concernés :\n${lines}`)
}

interface Draft {
  id: string | null
  name: string
  kind: CatalogKind
  description: string
  locator: string
  visibility: CatalogVisibility
  shares: CatalogShare[]
  owned: boolean
}

function emptyDraft(): Draft {
  return { id: null, name: '', kind: 'document', description: '', locator: '', visibility: 'private', shares: [], owned: true }
}

function fromResource(resource: CatalogResource): Draft {
  return {
    id: resource.id,
    name: resource.name,
    kind: resource.kind,
    description: resource.description,
    locator: resource.locator,
    visibility: resource.visibility,
    shares: resource.shares,
    owned: resource.owned,
  }
}

/**
 * * Catalog of resources available to models and instances.
 */
export function ResourcesPage() {
  const queryClient = useQueryClient()
  const catalog = useQuery<CatalogGroups>('catalog-resources', () => catalogService.list())
  const [draft, setDraft] = useState<Draft | null>(null)
  const [notice, setNotice] = useState('')
  const [blocked, setBlocked] = useState<CatalogUse[] | null>(null)

  const refresh = () => queryClient.invalidateQueries('catalog-resources')

  const save = async (confirmUsage = false) => {
    if (!draft || draft.name.trim() === '') {
      setNotice('Le nom est requis.')
      return
    }
    const payload = {
      name: draft.name,
      kind: draft.kind,
      description: draft.description,
      locator: draft.locator,
      visibility: draft.visibility,
      shares: draft.shares,
      confirmUsage,
    }
    try {
      if (draft.id) {
        await catalogService.update(draft.id, payload)
      } else {
        await catalogService.create(payload)
      }
      setDraft(null)
      setNotice('')
      setBlocked(null)
      await refresh()
    } catch (error) {
      const usage = usageOf(error)
      if (usage && !confirmUsage && confirmTwice(usage)) {
        await save(true)
        return
      }
      if (usage) {
        setBlocked(usage)
      }
      setNotice(errorMessage(error))
    }
  }

  const remove = async (resource: CatalogResource) => {
    setBlocked(null)
    try {
      await catalogService.remove(resource.id)
      await refresh()
    } catch (error) {
      const usage = usageOf(error)
      setBlocked(usage)
      setNotice(usage ? 'Suppression impossible : des agents déployés utilisent cette ressource.' : errorMessage(error))
    }
  }

  return (
    <div className="page-content relative min-h-screen">
      <div className="background-image"><img src="/logo.jpg" alt="" /></div>
      <div className="content-wrapper space-y-4">
        <h1 className="text-3xl font-extrabold tracking-tight tab_title">Ressources</h1>
        <p className="text-sm text-secondary">
          Une ressource ajoutée ici est la même partout : modèles d'équipes, cercles et instances.
          Cochez-la pour la proposer dans les autres onglets.
        </p>
        {notice ? <p className="text-sm text-error whitespace-pre-wrap">{notice}</p> : null}
        {blocked ? <UsageList usage={blocked} /> : null}
        <button type="button" className="button button--primary" onClick={() => setDraft(emptyDraft())}>Ajouter une ressource</button>
        {draft ? (
          <ResourceForm
            draft={draft}
            onChange={setDraft}
            onCancel={() => setDraft(null)}
            onSave={() => void save(false)}
          />
        ) : null}
        {catalog.isLoading ? <p className="text-sm text-secondary">Chargement…</p> : null}
        <ResourceTable title="Mes ressources" rows={catalog.data?.mine ?? []} onPick={refresh} onEdit={(resource) => setDraft(fromResource(resource))} onDelete={(resource) => void remove(resource)} />
        <ResourceTable title="Ressources partagées avec moi" rows={catalog.data?.shared ?? []} onPick={refresh} onEdit={(resource) => setDraft(fromResource(resource))} onDelete={null} />
        <ResourceTable title="Ressources publiques" rows={catalog.data?.public ?? []} onPick={refresh} onEdit={null} onDelete={null} />
      </div>
    </div>
  )
}

function UsageList({ usage }: { usage: CatalogUse[] }) {
  return (
    <div className="card space-y-2">
      <p className="text-sm font-medium text-primary">Rôles et instances qui utilisent cette ressource</p>
      <ul className="text-sm space-y-1">
        {usage.map((item) => (
          <li key={`${item.instanceId}-${item.roleId}`}>
            <Link className="text-primary underline" to={`/teams/circles?team=${item.templateId}&circle=${item.sourceCircleId || item.circleId}&role=${item.sourceRoleId || item.roleId}`}>{item.roleName}</Link>
            {' · '}
            <Link className="text-primary underline" to={`/instances/equipes#instance-${item.instanceId}`}>{item.instanceName}</Link>
            {` · ${item.agentName}`}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ResourceForm({
  draft,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft
  onChange: (draft: Draft) => void
  onCancel: () => void
  onSave: () => void
}) {
  return (
    <form className="card space-y-3" onSubmit={(event) => { event.preventDefault(); onSave() }}>
      <h2 className="text-lg font-semibold text-primary">{draft.id ? 'Modifier la ressource' : 'Nouvelle ressource'}</h2>
      <label className="block text-sm text-secondary" htmlFor="catalog-name">
        Nom
        <input id="catalog-name" name="catalog-name" className="form-input mt-1" value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} required />
      </label>
      <label className="block text-sm text-secondary" htmlFor="catalog-kind">
        Type
        <select id="catalog-kind" name="catalog-kind" className="form-input mt-1" value={draft.kind} onChange={(event) => onChange({ ...draft, kind: event.target.value as CatalogKind })}>
          {KINDS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
      </label>
      <label className="block text-sm text-secondary" htmlFor="catalog-description">
        Description
        <textarea id="catalog-description" name="catalog-description" className="form-input mt-1" rows={2} value={draft.description} onChange={(event) => onChange({ ...draft, description: event.target.value })} />
      </label>
      <label className="block text-sm text-secondary" htmlFor="catalog-locator">
        Adresse
        <input id="catalog-locator" name="catalog-locator" className="form-input mt-1" value={draft.locator} onChange={(event) => onChange({ ...draft, locator: event.target.value })} placeholder="https://" />
      </label>
      {draft.owned ? (
      <>
      <label className="block text-sm text-secondary" htmlFor="catalog-visibility">
        Visibilité
        <select id="catalog-visibility" name="catalog-visibility" className="form-input mt-1" value={draft.visibility} onChange={(event) => onChange({ ...draft, visibility: event.target.value as CatalogVisibility })}>
          {VISIBILITIES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
      </label>
      {draft.visibility === 'shared' ? (
        <div className="space-y-2">
          <p className="text-sm text-secondary">Partages (le nom d'utilisateur, lecture ou écriture)</p>
          {draft.shares.map((share, index) => (
            <div key={`${share.username}-${index}`} className="flex gap-2">
              <input className="form-input" aria-label="Nom d'utilisateur" value={share.username} onChange={(event) => {
                const shares = draft.shares.slice()
                const current = shares[index]
                if (current) {
                  shares[index] = { ...current, username: event.target.value }
                }
                onChange({ ...draft, shares })
              }} />
              <select className="form-input" aria-label="Droit de partage" value={share.level} onChange={(event) => {
                const shares = draft.shares.slice()
                const current = shares[index]
                if (current) {
                  shares[index] = { ...current, level: event.target.value === 'write' ? 'write' : 'read' }
                }
                onChange({ ...draft, shares })
              }}>
                <option value="read">Lecture</option>
                <option value="write">Écriture</option>
              </select>
            </div>
          ))}
          <button type="button" className="button button--secondary" onClick={() => onChange({ ...draft, shares: [...draft.shares, { username: '', level: 'read' }] })}>Ajouter un partage</button>
        </div>
      ) : null}
      </>
      ) : null}
      <div className="flex gap-2">
        <button type="submit" className="button button--primary">Enregistrer</button>
        <button type="button" className="button button--secondary" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}

function ResourceTable({
  title,
  rows,
  onPick,
  onEdit,
  onDelete,
}: {
  title: string
  rows: CatalogResource[]
  onPick: () => Promise<void>
  onEdit: ((resource: CatalogResource) => void) | null
  onDelete: ((resource: CatalogResource) => void) | null
}) {
  return (
    <section className="card overflow-hidden">
      <h2 className="text-lg font-semibold text-primary px-4 py-3">{title}</h2>
      {rows.length === 0 ? <p className="text-sm text-secondary px-4 pb-4">Aucune ressource.</p> : (
        <table className="w-full">
          <thead>
            <tr className="table-header">
              <th className="px-4 py-2 text-left text-xs">Disponible</th>
              <th className="px-4 py-2 text-left text-xs">Nom</th>
              <th className="px-4 py-2 text-left text-xs">Type</th>
              <th className="px-4 py-2 text-left text-xs">Visibilité</th>
              <th className="px-4 py-2 text-right text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((resource) => (
              <tr key={resource.id} className="table-row">
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Disponible pour les modèles : ${resource.name}`}
                    checked={resource.picked}
                    onChange={(event) => {
                      void catalogService.pick(resource.id, event.target.checked).then(onPick)
                    }}
                  />
                </td>
                <td className="px-4 py-2 text-sm text-primary">{resource.name}</td>
                <td className="px-4 py-2 text-sm text-secondary">{kindLabel(resource.kind)}</td>
                <td className="px-4 py-2 text-sm text-secondary">{visibilityLabel(resource.visibility)}</td>
                <td className="px-4 py-2 text-right">
                  {onEdit && resource.canEdit ? (
                    <button type="button" className="action-button action-button--primary" title="Modifier" onClick={() => onEdit(resource)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  ) : null}
                  {onDelete && resource.canDelete ? (
                    <button type="button" className="action-button action-button--danger" title="Supprimer" onClick={() => onDelete(resource)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
